// Week 1 / Day 3 — Nimny's Bank House: Account, Card and Secret Code.
// Reworked: bilingual RU/EN inline; playful Nimny mission tone;
// 50% online + 50% paper/parent. Five layers preserved.
// SAFETY: fictional accounts/cards only; no real bank, no PII, no real card data.

const day3 = {
  id: 'day-3',
  studentId: 'max',
  weekId: 'week-1',
  dayId: 'day-3',
  status: 'active',
  title: 'Домик банка Нимни: счёт, карта и секретный код / Nimny\'s Bank House: Account, Card and Secret Code',
  shortTitle: 'Счёт и карта / Account and Card',
  theme:
    'Счёт помогает вести запись, а секретные коды помогают защищать деньги. / An account helps keep a record, and secret codes help protect money.',
  estimatedTimeLabel:
    'Онлайн-миссия: около 20–30 минут. Бумажная миссия с родителем: около 25–35 минут. / Online mission: about 20–30 minutes. Paper mission with parent: about 25–35 minutes.',
  nimnyIntro:
    'Нимни нашла маленький домик банка. Внутри есть счёт, баланс, карточка и секретный код. Помоги Нимни понять, что можно показывать, а что нужно хранить в секрете. / Nimny found a tiny bank house. Inside are an account, a balance, a card and a secret code. Help Nimny understand what can be shown and what must stay secret.',
  goals: [
    'Слой 1. Математика: считать баланс счёта, делить нимы между «каждый день» и «целью», вести таблицу проверок. / Layer 1. Math: track an account balance, split nims between "everyday" and "goal", keep a check table.',
    'Слой 2. Русский язык и правописание: словарь безопасности, мини-диктант о PIN/пароле, исправление коротких предложений. / Layer 2. Russian language and spelling: safety vocabulary, mini-dictation about PIN/password, sentence correction.',
    'Слой 3. Чтение, письмо и пересказ: «Что такое счёт?» — план, пересказ и 3–5 предложений о правилах безопасности. / Layer 3. Reading, writing and retelling: "What is an account?" — plan, retell and 3–5 sentences about safety rules.',
    'Слой 4. Наблюдение и логика — что можно показывать, а что хранить в секрете. / Layer 4. Observation and Logic — What Can Be Shown and What Must Stay Secret.',
    'Слой 5. Практика на бумаге: «Карта безопасности Нимни» и две коробочки счёта. / Layer 5. Paper practice: "Nimny Safety Map" and two account boxes.',
    'Дневник мышления: одно правило безопасности дня. / Thinking diary: one safety rule of the day.',
    'Награда нимами: базово 3 нима, максимум 5 с бонусами. / Reward in nims: 3 base, up to 5 with bonuses.'
  ],
  vocabulary: [
    { term: 'банк / bank', definition: 'место, где хранят запись нимов на счетах. / a place that keeps records of nims in accounts.' },
    { term: 'счёт / account', definition: 'запись, в которой видно баланс и движение нимов. / a record that shows balance and movement of nims.' },
    { term: 'баланс / balance', definition: 'число нимов на счёте сейчас. / the number of nims on the account now.' },
    { term: 'карта / card', definition: 'инструмент, который помогает использовать счёт. / a tool that helps use the account.' },
    { term: 'PIN / PIN', definition: 'секретное число для карты. / a secret number for the card.' },
    { term: 'пароль / password', definition: 'секретное слово для входа. / a secret word to log in.' },
    { term: 'секрет / secret', definition: 'то, что не сообщают другим. / something not shared with others.' },
    { term: 'безопасно / safe', definition: 'когда нет риска для счёта. / when the account is not at risk.' },
    { term: 'опасно / unsafe', definition: 'когда счёт можно потерять. / when the account could be lost.' },
    { term: 'взрослый / adult', definition: 'тот, у кого можно спросить совета. / someone you can ask for advice.' }
  ],
  onlineTasks: [
    {
      id: 'q_w1',
      type: 'math',
      title: 'Разогрев 1 / Warm-up 1',
      prompt: 'Десятичный счёт: 1.5 + 2.5 + 3.0 = ? / Decimal warm-up: 1.5 + 2.5 + 3.0 = ?',
      helper: 'Сгруппируй: 1.5 + 2.5 = 4; 4 + 3 = 7. / Group: 1.5 + 2.5 = 4; 4 + 3 = 7.',
      answer: '7',
      unit: 'нимов / nims'
    },
    {
      id: 'q_w2',
      type: 'math',
      title: 'Разогрев 2 / Warm-up 2',
      prompt: 'Десятичный счёт: 10 − 2.5 − 1.25 = ? / Decimal warm-up: 10 − 2.5 − 1.25 = ?',
      helper: '10 − 2.5 = 7.5; 7.5 − 1.25 = 6.25. / 10 − 2.5 = 7.5; 7.5 − 1.25 = 6.25.',
      answer: '6.25',
      unit: 'нимов / nims'
    },
    {
      id: 'q1',
      type: 'math',
      title: 'Баланс с комиссией / Balance with a fee',
      prompt:
        'На счёте 520 нимов. Покупка 125 нимов, комиссия 2% от покупки, потом возврат 45 нимов. Какой баланс? / Account 520 nims. Purchase 125, fee 2% of purchase, then refund 45. What is the balance?',
      helper:
        'Что сделать: 1) найди 2% от 125; 2) вычти покупку и комиссию; 3) добавь возврат. / What to do: 1) find 2% of 125; 2) subtract purchase and fee; 3) add the refund.',
      answer: '437.50',
      unit: 'нимов / nims'
    },
    {
      id: 'q2',
      type: 'math',
      title: 'Уравнение карточек-памяток / Memory cards equation',
      prompt:
        'Нимни сделала 4 одинаковые безопасные карточки-памятки и ещё 12 наклеек. Всего 100 элементов. Сколько элементов в одной карточке? / Nimny made 4 identical safety memory cards plus 12 stickers. 100 elements total. How many elements per card?',
      helper:
        'Что сделать: 4x + 12 = 100. Найди x. / What to do: 4x + 12 = 100. Solve for x.',
      answer: '22',
      unit: 'элементов / elements'
    },
    {
      id: 'q3',
      type: 'math',
      title: 'Сравнение комиссий / Compare fees',
      prompt:
        'Вариант A: комиссия 1.5% от 200 нимов. Вариант B: фиксированная комиссия 4 нима. Какой вариант меньше и на сколько нимов? / Option A: 1.5% fee of 200 nims. Option B: flat 4-nim fee. Which is smaller and by how many nims?',
      helper:
        'Что сделать: посчитай комиссию A (процент от 200), сравни с 4 и найди разницу. Ответ — число «на сколько меньше». / What to do: compute A as percent of 200, compare with 4, write the difference.',
      answer: '1',
      unit: 'ним / nim'
    },
    {
      id: 'q4',
      type: 'math',
      title: 'Среднее бонусов / Average of bonuses',
      prompt:
        'За 4 недели Макс получил бонусы 1.25, 1.75, 0.50 и 2.50 нима. Какое среднее за неделю? / Over 4 weeks Max received bonuses 1.25, 1.75, 0.50 and 2.50 nims. What is the weekly average?',
      helper:
        'Что сделать: сложи все бонусы и раздели на 4. / What to do: sum all bonuses and divide by 4.',
      answer: '1.5',
      unit: 'нима / nims'
    },
    {
      id: 'q_s1',
      type: 'math',
      title: 'Растяжка 1 / Stretch 1',
      prompt: 'Найди 0.5% от 1000 нимов цели. / Find 0.5% of a 1000-nim goal.',
      helper: '1% от 1000 = 10; 0.5% = половина от 1% = 5. / 1% of 1000 = 10; 0.5% = 5.',
      answer: '5',
      unit: 'нимов / nims'
    },
    {
      id: 'q_s2',
      type: 'math',
      title: 'Растяжка 2 / Stretch 2',
      prompt: 'Найди 1/8 от 200 нимов. / Find 1/8 of 200 nims.',
      helper: '1/8 = деление на 8; 200 ÷ 8 = 25. / 1/8 = divide by 8; 200 ÷ 8 = 25.',
      answer: '25',
      unit: 'нимов / nims'
    },
    {
      id: 'q5',
      type: 'classify',
      title: 'Слова безопасности: безударные и разделительные / Safety words: vowels and separators',
      prompt:
        'Какая буква пишется на месте пропуска? / Which letter goes in the blank?',
      categories: [
        { id: 'tv', label: 'разделительный ъ / hard sign ъ' },
        { id: 'sft', label: 'разделительный ь / soft sign ь' },
        { id: 'vw', label: 'безударная гласная / unstressed vowel' }
      ],
      items: [
        { id: 'objyasnit', text: 'об_яснить — проверка: разделение приставки и корня / "objasnit\'" — separator after prefix' },
        { id: 'sjest', text: 'с_есть — проверка: разделение перед е / "sjest\'" — separator before е' },
        { id: 'druzya', text: 'друз_я — проверка: разделение перед я / "druz\'ya" — separator before я' },
        { id: 'sekret', text: 'с_крет — проверка: секретный / "sekret" — check via "секретный"' },
        { id: 'parol', text: 'п_роль — проверка: пароли / "parol\'" — check via "пароли"' },
        { id: 'bezopasno', text: 'б_зопасно — проверка: без + опасно / "bezopasno" — root prefix без' }
      ]
    },
    {
      id: 'q6',
      type: 'text',
      title: 'Мини-диктант / Mini-dictation',
      prompt:
        'Запиши без ошибок: «Пароль и PIN нельзя сообщать другим людям. Если ситуация странная, нужно спросить взрослого.» / Write without mistakes: "Password and PIN must not be shared. If a situation feels strange, ask an adult."',
      placeholder:
        'Пароль и PIN ... / Password and PIN ...'
    },
    {
      id: 'q_corr',
      type: 'text',
      title: 'Исправь ошибки / Correct the mistakes',
      prompt:
        'Перепиши без ошибок: «пороль и пин нельзя сообщят другим людям. еслi ситуация страная нужна спрасить взрослого.» Подсказки: «пароль», «PIN» — заглавные / правописание; «сообщать», «спросить» — мягкий знак (что делать?); «странная» — две «н»; «нужно» — наречие на -о; запятая после «странная». / Rewrite: fix soft signs, doubled «н», capitalization and punctuation.',
      placeholder:
        'Пароль и PIN нельзя сообщать другим людям. Если ситуация странная, нужно спросить взрослого. / Password and PIN ...'
    },
    {
      id: 'q7',
      type: 'text',
      title: 'План текста и письмо / Text plan and writing',
      prompt:
        'Прочитай короткий текст: «Счёт — это запись нимов в банке. Баланс показывает, сколько на счёте сейчас. Карта помогает пользоваться счётом, а PIN и пароль — секреты. Если ситуация странная, нужно спросить взрослого.» Запиши план из 3 пунктов и 3–5 предложений на русском по теме: «Какие правила безопасности я запомнил?» / Read the short text: "An account is a record of nims at the bank. Balance shows what is on the account now. A card helps use the account; PIN and password are secrets. If a situation feels strange, ask an adult." Write a 3-point plan and 3–5 Russian sentences.',
      placeholder:
        '1. ... 2. ... 3. ... Мои предложения: ... / 1. ... 2. ... 3. ... My sentences: ...'
    },
    {
      id: 'q_obs',
      type: 'text',
      title: 'Наблюдение и логика — секрет или можно показать / Observation and Logic — Secret or Public',
      prompt:
        'Подумай и запиши:\nЧто можно показать (3 примера): ____, ____, ____\nЧто нужно хранить в секрете (3 примера): ____, ____, ____\nЧто делать при странном запросе? ____\nЧто меняется, когда мы делимся секретом? ____\nЧто остаётся в безопасности при правиле «спросить взрослого»? ____\nСделай вывод одной фразой. / Public vs secret (3 examples each); action on a strange request; what changes when we share a secret; what stays safe with the "ask an adult" rule; one-line conclusion.',
      placeholder:
        'Можно: ... Секрет: ... Странный запрос: ... При утечке: ... При «спросить»: ... Вывод: ... / Public: ... Secret: ... Strange: ... Leak: ... Ask: ... Conclusion: ...'
    },
    {
      id: 'q8',
      type: 'classify',
      title: 'Карта безопасности: безопасно / опасно / спросить взрослого / Safety map: safe / unsafe / ask an adult',
      prompt:
        'Распредели карточки по корзинам безопасности. / Sort the cards into safety baskets.',
      categories: [
        { id: 'safe', label: 'Безопасно / Safe' },
        { id: 'unsafe', label: 'Опасно / Unsafe' },
        { id: 'ask', label: 'Спросить взрослого / Ask an adult' }
      ],
      items: [
        { id: 'cover', text: 'прикрыть PIN рукой / cover the PIN with your hand' },
        { id: 'tellfriend', text: 'сказать пароль другу / tell the password to a friend' },
        { id: 'strange', text: 'получил странное сообщение / got a strange message' },
        { id: 'askadult', text: 'спросить родителя / ask a parent' },
        { id: 'unknownsite', text: 'ввести карту на незнакомом сайте / enter card on an unknown site' },
        { id: 'checksrc', text: 'проверить источник / check the source' },
        { id: 'readterms', text: 'читать условия вместе с родителем / read the terms with a parent' },
        { id: 'ownaccount', text: 'открыть счёт самому / open an account by yourself' }
      ]
    },
    {
      id: 'q9',
      type: 'essay',
      title: 'Дневник мышления: моё правило безопасности / Thinking diary: my safety rule',
      prompt:
        'Напиши 3–5 предложений: какое правило безопасности я выбрал и почему. Используй слова: счёт, баланс, секрет, безопасно, спросить взрослого. / Write 3–5 sentences: which safety rule I chose and why. Use words: account, balance, secret, safe, ask an adult.',
      minSentences: 3,
      maxSentences: 5,
      placeholder:
        'Сегодня я выбрал правило ... / Today I chose the rule ...'
    }
  ],
  notebookInstructions: [
    'Заголовок: «Неделя 1, День 3 — Домик банка Нимни». / Heading: "Week 1, Day 3 — Nimny\'s Bank House".',
    'Слой 1. Математика: реши q1–q4, нарисуй таблицу проверок баланса. / Layer 1. Math: solve q1–q4 and draw a balance-check table.',
    'Слой 2. Русский язык: запиши словарь безопасности и мини-диктант. / Layer 2. Russian: write the safety vocabulary and the mini-dictation.',
    'Слой 3. Чтение и пересказ: 3-пунктовый план + 3–5 предложений; перескажи родителю. / Layer 3. Reading and retelling: 3-point plan + 3–5 sentences; retell to a parent.',
    'Слой 4. Наблюдение и логика: запиши 3 примера секретного и 3 примера открытого. / Layer 4. Observation and Logic: write 3 examples of secret and 3 examples of public.',
    'Слой 5. Практика: разложи карту безопасности и заполни две коробочки. / Layer 5. Practice: sort the safety map and fill the two boxes.',
    'Дневник мышления: одно правило безопасности. / Thinking diary: one safety rule.',
    'Награда нимами начисляется после проверки родителем. / Reward in nims is added after a parent check.'
  ],
  printWorksheet: {
    title: 'Неделя 1, День 3 / Week 1, Day 3',
    subtitle: 'Домик банка Нимни / Nimny\'s Bank House',
    pages: [
      {
        title: 'Маршрут миссии — Домик банка Нимни / Mission Route — Nimny\'s Bank House',
        blocks: [
          { type: 'text', text: 'Сегодня — пять слоёв и одна миссия. Отмечай ☐ → ☑. / Today — five layers and one mission. Mark ☐ → ☑.' },
          { type: 'list', items: [
            '☐ Слой 1. Математика — около 10 минут. / Layer 1. Math — about 10 minutes.',
            '☐ Слой 2. Русский язык — около 10 минут. / Layer 2. Russian — about 10 minutes.',
            '☐ Слой 3. Чтение и пересказ — около 10 минут. / Layer 3. Reading and retelling — about 10 minutes.',
            '☐ Слой 4. Наблюдение и логика — около 5 минут. / Layer 4. Observation and Logic — about 5 minutes.',
            '☐ Слой 5. Практика на бумаге — около 15 минут. / Layer 5. Paper practice — about 15 minutes.',
            '☐ Дневник мышления — 5 минут. / Thinking diary — 5 minutes.',
            '☐ Печать миссии и награда нимами. / Mission stamp and nim reward.'
          ]}
        ]
      },
      {
        title: 'Слой 1. Математика — комиссии и уравнение / Layer 1. Math — Fees and Equation',
        blocks: [
          { type: 'text', text: 'Считай в нимах. Записывай шаги. / Count in nims. Show your steps.' },
          { type: 'list', items: [
            '1) Баланс с комиссией: счёт 520; покупка 125; комиссия 2% от 125; возврат 45. / Balance with fee.',
            '   2% × 125 = ____ . 520 − 125 − ____ + 45 = ____ нимов / nims.',
            '2) Уравнение: 4x + 12 = 100. Найди x. / Equation.',
            '   4x = ____  →  x = ____ элементов / elements.',
            '3) Сравнение комиссий: A = 1.5% от 200; B = 4 нима. / Compare fees.',
            '   A = ____ . Меньше: вариант ____ . Разница: ____ нима / nim.',
            '4) Разогрев: 1.5 + 2.5 + 3.0 = ____ ; 10 − 2.5 − 1.25 = ____ . / Decimal warm-up.',
            '5) Растяжка: 0.5% от 1000 нимов цели = ____ ; 1/8 от 200 = ____ .'
          ]}
        ]
      },
      {
        title: 'Слой 2. Русский язык и правописание — слова безопасности / Layer 2. Russian Language and Spelling — Safety Words',
        blocks: [
          { type: 'text', text: 'Правило дня: разделительные ъ и ь стоят перед е, ё, ю, я (объяснить, друзья). / Rule of the day: separators ъ and ь stand before е, ё, ю, я.' },
          { type: 'list', items: [
            'Словарь миссии: банк, счёт, баланс, карта, PIN, пароль, секрет, безопасно, опасно, взрослый. / Mission vocabulary.',
            'Мини-диктант: «Пароль и PIN нельзя сообщать другим людям. Если ситуация странная, нужно спросить взрослого.» / Mini-dictation.',
            'Запиши без ошибок: __________________________________________ / Write without mistakes:',
            'Исправление: «пороль и пин нельзя сообщят другим людям. еслi ситуация страная нужна спрасить взрослого.» / Correct and rewrite the sentence with mistakes.',
            'Подчеркни разделительные ъ/ь в словах: объяснить, съесть, друзья. / Underline ъ/ь.'
          ]}
        ]
      },
      {
        title: 'Слой 3. Чтение, письмо и пересказ — что такое счёт / Layer 3. Reading, Writing and Retelling — What Is an Account',
        blocks: [
          { type: 'text', text: 'Прочитай короткий текст и сделай план. / Read the short text and make a plan.' },
          { type: 'list', items: [
            'Текст: «Счёт — это запись нимов в банке. Баланс показывает, сколько на счёте сейчас. Карта помогает пользоваться счётом, а PIN и пароль — секреты. Если ситуация странная, нужно спросить взрослого.» / Text: "An account is a record of nims..."',
            'План: 1) ____________________  2) ____________________  3) ____________________ / Plan',
            'Главная мысль: ______________________________________ / Main idea',
            'Мои 3–5 предложений «Какие правила безопасности я запомнил?»: / My 3–5 sentences',
            '1. ______________________________________________',
            '2. ______________________________________________',
            '3. ______________________________________________',
            '☐ Я пересказал родителю / I retold a parent.'
          ]}
        ]
      },
      {
        title: 'Слой 4. Наблюдение и логика — секрет или можно показать? / Layer 4. Observation and Logic — Secret or Public?',
        blocks: [
          { type: 'text', text: 'Понаблюдай: что можно показывать, а что хранить в секрете. / Observe: what can be shown and what must stay secret.' },
          { type: 'list', items: [
            'Можно показать (3 примера): ____________________ / Can be shown',
            'Нужно хранить в секрете (3 примера): ____________________ / Must stay secret',
            'Если запрос странный, что нужно сделать? ____________________ / If a request feels strange, what to do?',
            'Что меняется, когда мы делимся секретом? ____________________ / What changes when we share a secret?',
            'Что остаётся в безопасности при правиле «спросить взрослого»? ____________________ / What stays safe with the "ask an adult" rule?',
            'Мой вывод (1 фраза): ____________________ / My conclusion (1 line):'
          ]}
        ]
      },
      {
        title: 'Слой 5. Практика на бумаге — Карта безопасности и две коробочки / Layer 5. Paper Practice — Safety Map and Two Boxes',
        blocks: [
          { type: 'text', text: 'Нарежь или отметь карточки. Разложи их в 3 корзины. / Cut or mark the cards. Sort into 3 baskets.' },
          { type: 'list', items: [
            'Корзины: «Безопасно», «Опасно», «Спросить взрослого». / Baskets: Safe, Unsafe, Ask an adult.',
            'Карточки: прикрыть PIN рукой; сказать пароль другу; странное сообщение; спросить родителя; ввести карту на незнакомом сайте; проверить источник; читать условия с родителем; открыть счёт самому. / Cards.',
            '☐ Обведи 3 безопасные карточки. / Circle 3 safe cards.',
            '☐ Обведи 1 опасную карточку красным. / Circle 1 unsafe card in red.',
            '☐ Обведи 1 «спросить взрослого». / Circle 1 "ask an adult".',
            'Две коробочки счёта (старт 90 нимов): / Two account boxes (start 90 nims):',
            '☐ Коробка «На каждый день»: ____________________ (____ нимов) / "Everyday" box',
            '☐ Коробка «На цель»: ____________________ (____ нимов) / "Goal" box',
            'Моё правило проверки баланса: ____________________ / My balance-check rule:',
            'Моё правило безопасности: ____________________ / My safety rule:'
          ]}
        ]
      },
      {
        title: 'Дневник мышления / Thinking Diary',
        blocks: [
          { type: 'list', items: [
            'Самое важное правило безопасности сегодня: ______________________ / Most important safety rule today',
            'Я выбрал его, потому что _________________________ / I chose it because',
            'Слово миссии, которое запомнилось: ________________ / Mission word I remember'
          ]}
        ]
      },
      {
        title: 'Родительская проверка и награда нимами / Parent Check and Nim Reward',
        blocks: [
          { type: 'text', text: 'После проверки начисли бонусные нимы через Parent Room. / After checking, add bonus nims through Parent Room.' },
          { type: 'list', items: [
            'Базовая награда: 3 нима ☐ / Base reward: 3 nims ☐',
            'Бонус за безопасные решения: +1 ☐ / Safe-choice bonus: +1 ☐',
            'Бонус за ясное объяснение безопасности: +1 ☐ / Clear-safety-explanation bonus: +1 ☐',
            'Итого: ____ из 5 нимов. / Total ____ of 5 nims.',
            'Печать миссии (родитель): ☐ / Mission stamp (parent): ☐',
            'Подпись родителя: ____________________ / Parent signature'
          ]}
        ]
      }
    ]
  },
  parentGuide: {
    goal:
      'Ввести понятие счёта как записи и показать, что секретные коды защищают деньги. Всё в безопасных, бумажных, фиктивных сценариях. / Introduce the account as a record and show that secret codes protect money. All in safe, paper, fictional scenarios.',
    mathGoal:
      'Баланс с процентной комиссией (520 − 125 − 2.50 + 45 = 437.50), уравнение 4x + 12 = 100 (x = 22) и сравнение комиссий (A = 1.5% × 200 = 3, меньше B на 1 ним). / Balance with a percent fee, equation 4x + 12 = 100, and fee comparison.',
    russianGoal:
      'Разделительные ъ/ь и/или повторение Дня 1; правильно записанный мини-диктант; исправлены 3–5 коротких предложений. / ъ/ь separators and/or Day 1 review; correct mini-dictation; 3–5 sentences corrected.',
    readingWritingGoal:
      'План из 3 пунктов; пересказ родителю; 3–5 предложений на русском по теме «правила безопасности». / 3-point plan; retell to a parent; 3–5 Russian sentences about safety rules.',
    practicalGoal:
      'Карточки разложены в 3 корзины (безопасно/опасно/спросить взрослого); две коробочки счёта заполнены; сформулированы 1 правило проверки баланса и 1 правило безопасности. / Cards sorted into 3 baskets; two account boxes filled; one balance-check rule and one safety rule formulated.',
    observationGoal:
      'Ребёнок различает «можно показать» и «нужно держать в секрете»; знает действие при странной ситуации — спросить взрослого. / Child distinguishes "public" vs "secret" and knows the action for a strange situation — ask an adult.',
    check: [
      'Q1: 437.50 нимов (с комиссией). Q2: x = 22 элемента (уравнение). Q3: вариант A меньше на 1 ним (A = 3, B = 4). Q4: 12 проверок. / Q1: 437.50. Q2: 22. Q3: A − 1. Q4: 12.',
      'Мини-диктант записан без грубых ошибок. / Mini-dictation OK.',
      'План текста — 3 пункта; пересказ — 3–5 предложений по-русски. / Plan = 3 points; retell = 3–5 Russian sentences.',
      'Карта безопасности: 3 «безопасно», 1 «опасно», 1 «спросить взрослого» обведены. / Safety map: 3 safe + 1 unsafe + 1 ask circled.',
      'Две коробочки счёта заполнены, сумма не превышает 90 нимов. / Two boxes filled within 90 nims.',
      'Дневник мышления заполнен; есть одно правило безопасности. / Thinking diary has one safety rule.'
    ],
    answerKey: [
      { taskId: 'q_w1', answer: 'Answer: 7 нимов. Solution: 1.5 + 2.5 = 4; 4 + 3 = 7. Parent note: десятичные удобно группировать парами. / Decimal warm-up.' },
      { taskId: 'q_w2', answer: 'Answer: 6.25 нима. Solution: 10 − 2.5 = 7.5; 7.5 − 1.25 = 6.25. Parent note: десятичное вычитание с заёмом. / Decimal warm-up 2.' },
      { taskId: 'q1', answer: 'Answer: 437.50 нимов. Solution: 2% × 125 = 2.50; 520 − 125 − 2.50 + 45 = 437.50. Parent note: процент считаем ДО вычитания; возврат прибавляется в конце. / Steps: 2.50; 520 − 125 − 2.50 + 45 = 437.50.' },
      { taskId: 'q2', answer: 'Answer: 22 элемента. Solution: 4x + 12 = 100 → 4x = 88 → x = 22. Parent note: «лишние 12» — наклейки, их вычитаем первыми; остаток делим на 4. / Equation 4x + 12 = 100, x = 22.' },
      { taskId: 'q3', answer: 'Answer: вариант A меньше на 1 ним. Solution: A = 1.5% × 200 = 3; B = 4; B − A = 1. Parent note: 1% от 200 = 2, значит 1.5% = 3. / A = 3, B = 4, diff = 1.' },
      { taskId: 'q4', answer: 'Answer: 1.5 нима в неделю. Solution: 1.25 + 1.75 + 0.50 + 2.50 = 6.0; 6.0 ÷ 4 = 1.5. Parent note: среднее = сумма ÷ количество значений; десятичный ответ принят. / Average decimal = 1.5.' },
      { taskId: 'q_s1', answer: 'Answer: 5 нимов. Solution: 1% × 1000 = 10; 0.5% = половина = 5. Parent note: процент < 1 — лучше через «половину 1%», а не через 0.005 × 1000. / 0.5% of 1000 = 5.' },
      { taskId: 'q_s2', answer: 'Answer: 25 нимов. Solution: 200 ÷ 8 = 25. Parent note: 1/8 — это «деление на 8»; полезный мост к 12.5%. / 1/8 of 200 = 25.' },
      { taskId: 'q5', answer: 'Answer: объяснить — ъ; съесть — ъ; друзья — ь; секрет — безударная «е» (проверка «секретный»); пароль — безударная «а» (проверка «пароли»); безопасно — приставка «без» + опасно (безударная «е»). Solution: ъ ставится после приставок перед е/ё/ю/я; ь — внутри корня или между корнями. Parent note: показать ребёнку разницу между «приставка+корень» (объяснить, съесть) и «корень+окончание» (друзья). / Separators rule + unstressed vowels.' },
      { taskId: 'q6', answer: 'Answer: «Пароль и PIN нельзя сообщать другим людям. Если ситуация странная, нужно спросить взрослого.» Solution: запятая после «странная»; точки. Parent note: ≤ 2 ошибок — нормально. / Mini-dictation: ≤ 2 errors acceptable.' },
      { taskId: 'q_corr', answer: 'Answer: «Пароль и PIN нельзя сообщать другим людям. Если ситуация странная, нужно спросить взрослого.» Solution: пороль→Пароль (заглавная + проверка: пароли); пин→PIN (английские заглавные); сообщят→сообщать (мягкий знак, что делать?); еслi→Если (русская «и» + заглавная); страная→странная (две «н»); нужна→нужно (наречие на -о); спрасить→спросить (приставка «с» + проверка: просит); запятая после «странная». Parent note: 7 ошибок — засчитывай ≥ 6/7. / Acceptance: ≥ 6/7.' },
      { taskId: 'q7', answer: 'Answer: 3-пунктовый план + 3–5 предложений. Solution: пример плана — 1) счёт = запись нимов в банке; 2) карта помогает использовать счёт, PIN/пароль = секрет; 3) при странной ситуации — спросить взрослого. Parent note: главное — мысль о секретности PIN/пароля и правиле «спросить». / 3-point plan; PIN secrecy + ask rule.' },
      { taskId: 'q_obs', answer: 'Answer: Можно показать — имя, школа, любимая книга. Секрет — PIN, пароль, дата рождения с адресом, ответы на «секретные вопросы». При странном запросе — спросить взрослого. При утечке секрета — счёт становится уязвим. При правиле «спросить взрослого» — родитель решает, безопасно или нет. Solution: вывод — «Что один раз сказал — может стать чужим. Поэтому секрет = молчание + взрослый.» Parent note: принимай вывод про необратимость утечки + защиту через взрослого. / Public/secret split; rule keeps account safe.' },
      { taskId: 'q8', answer: 'Answer: Безопасно — прикрыть PIN, спросить родителя, проверить источник, читать условия с родителем. Опасно — сказать пароль другу, ввести карту на незнакомом сайте, открыть счёт самому. Спросить взрослого — странное сообщение. Solution: «открыть счёт самому» — опасно по закону (нужны взрослые), а не по технической причине. Parent note: проговорите эту нюансу вслух. / Safe / Unsafe / Ask classifications.' },
      { taskId: 'q9', answer: 'Answer: открытый ответ. 3–5 предложений со словами: счёт, баланс, секрет, безопасно, спросить взрослого. Solution: пример — «Моё правило: PIN — это секрет. Я никому не говорю его. Если сообщение странное, спрошу взрослого. Так баланс на счёте остаётся в безопасности.» Parent note: проверь, что все 5 слов использованы. / Open answer with required words.' }
    ],
    spellingCheck:
      'Принимать, если ребёнок ставит ъ/ь правильно хотя бы в 2 из 3 слов и верно объясняет 2 из 3 безударных гласных. / Accept if at least 2 of 3 separators and 2 of 3 unstressed vowels are correct.',
    readingCheck:
      'План — 3 пункта; пересказ по-русски без чтения с листа. / 3-point plan; retell in Russian without reading.',
    practicalCheck:
      'Карта безопасности заполнена логично; 2 коробочки счёта в сумме не больше 90 нимов; правила сформулированы коротко и ясно. / Safety map sorted logically; two boxes total ≤ 90; rules concise and clear.',
    observationCheck:
      'Ребёнок называет 3 примера «открытого» и 3 примера «секретного» и говорит: «при странной ситуации — спросить взрослого». / Child names 3 public + 3 secret examples and the "ask an adult" rule.',
    discussionPrompts: [
      'Что такое баланс? / What is balance?',
      'Что должно оставаться в секрете? / What must stay secret?',
      'Что ты делаешь, если сообщение кажется странным? / What do you do if a message feels strange?',
      'Почему мы спрашиваем взрослого, прежде чем использовать настоящий сайт или карту? / Why do we ask an adult before using a real site or card?'
    ],
    ifEasy:
      'Усложни: попроси ребёнка написать 3 разных «секретных» примера и 3 «открытых» из жизни и объяснить разницу. / Ask the child to write 3 secret + 3 public examples from daily life and explain the difference.',
    ifDifficult:
      'Используй бытовые предметы как карточки: «дверной ключ» = «секрет», «номер дома» = «открыто». Поиграйте в сортировку вместе. / Use household items as cards: house key = secret, house number = public. Sort together.',
    tokenReward:
      'После проверки можно начислить бонусные нимы через Parent Room. Нимы — это призовые токены курса, а не оплата. / After checking the work, the parent may add bonus nims through Parent Room. Nims are course prize tokens, not payment.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 3,
    max: 5,
    label: 'Награда за миссию',
    display: '3 нима',
    bonuses: [
      { id: 'safe-choice', label: 'Бонус за безопасные решения', amount: 1 },
      { id: 'clear-safety-explanation', label: 'Бонус за ясное объяснение безопасности', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: ['q1', 'q5', 'q7', 'q8', 'q9'],
    allowNotebookCompletion: true
  },
  nextLessonId: 'max-week-1-day-4'
};

export default day3;
